import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';
import { FormBuilder, FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatSelectModule } from '@angular/material/select';
import { TranslateModule } from '@ngx-translate/core';
import { ICustomField, IObjectCustomFields } from 'src/app/features/settings/models/settings.models';
import { CustomFieldTypes, CustomFieldType } from 'src/app/features/settings/models/custom-fields.constants';
import { ButtonComponent } from 'src/app/ui/button/button.component';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { SvgIconComponent } from 'src/app/ui/svg-icon/svg-icon.component';
import { LanguageService } from 'src/app/config/language.service';
import { FormFieldComponent, SelectOption } from 'src/app/ui/form-field/form-field.component';
@Component({
  selector: 'app-object-custom-fields-form',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    TranslateModule,
    MatSelectModule,
    MatCheckboxModule,
    ButtonComponent,
    MatDatepickerModule,
    SvgIconComponent,
    FormFieldComponent,
  ],
  templateUrl: './object-custom-fields-form.component.html',
  styleUrls: ['./object-custom-fields-form.component.scss'],
})
export class ObjectCustomFieldsFormComponent implements OnChanges {
  @Input() objectCustomFields: IObjectCustomFields | null = null;
  currentLanguage: string = this.languageService.getCurrentLanguage() || 'en';
  @Input() showSaveButton: boolean = true;
  readonly customFieldTypes = CustomFieldTypes;

  @Output() save = new EventEmitter<IObjectCustomFields['values']>();

  form: FormGroup = this.fb.group({});
  definitions: ICustomField[] = [];

  constructor(private fb: FormBuilder, private languageService: LanguageService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['objectCustomFields']) {
      this.rebuildForm();
    }
  }

  rebuildForm(): void {
    const defs = this.objectCustomFields?.definitions ?? [];
    this.definitions = defs;

    const nextNames = new Set(defs.map((d) => d.technical_name));
    Object.keys(this.form.controls).forEach((name) => {
      if (!nextNames.has(name)) this.form.removeControl(name);
    });

    for (const def of defs) {
      const name = def.technical_name;
      const initialValue = this.getInitialValue(def);
      const validators = this.getValidators(def);

      if (this.form.contains(name)) {
        const ctrl = this.form.get(name) as FormControl;
        ctrl.setValidators(validators);
        ctrl.setValue(initialValue, { emitEvent: false });
        ctrl.updateValueAndValidity({ emitEvent: false });
      } else {
        this.form.addControl(name, new FormControl(initialValue, validators));
      }
    }
  }

  getLabel(def: ICustomField): string {
    return (
      def?.label?.[this.currentLanguage] ||
        // or first language that has value
      Object.values(def?.label ?? {}).find((value) => value !== '') ||
      def?.technical_name ||
      ''
    );
  }

  getInputType(fieldType: CustomFieldType): string {
    switch (fieldType) {
      case CustomFieldTypes.NUMBER:
      case CustomFieldTypes.DECIMAL:
        return 'number';
      case CustomFieldTypes.EMAIL:
        return 'email';
      case CustomFieldTypes.URL:
        return 'url';
      default:
        return 'text';
    }
  }

  getUiFormFieldType(fieldType: CustomFieldType): 'text' | 'number' | 'email' | 'url' {
    return this.getInputType(fieldType) as 'text' | 'number' | 'email' | 'url';
  }

  getSingleSelectOptions(def: ICustomField): SelectOption[] {
    const base = this.getAllowedValuesOptions(def);
    return [{ label: '', value: '' }, ...base];
  }

  getMultiSelectOptions(def: ICustomField): SelectOption[] {
    return this.getAllowedValuesOptions(def);
  }

  private getAllowedValuesOptions(def: ICustomField): SelectOption[] {
    const allowed = Array.isArray(def.allowed_values) ? def.allowed_values : [];
    return allowed.map((opt: any) => ({ label: (opt ?? '').toString(), value: opt as any }));
  }

  isControlInvalid(name: string): boolean {
    const ctrl = this.form.get(name);
    return !!ctrl && ctrl.invalid && (ctrl.touched || ctrl.dirty);
  }

  onSave(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid) return;
    this.save.emit(this.serializeValues());
  }

  private serializeValues(): IObjectCustomFields['values'] {
    const out: IObjectCustomFields['values'] = {};
    for (const def of this.definitions) {
      const name = def.technical_name;
      let v = this.form.get(name)?.value;

      // normalize date (Moment / Date / string) => YYYY-MM-DD
      if (def.field_type === CustomFieldTypes.DATE) {
        if (v && typeof v === 'object' && typeof (v as any).format === 'function') {
          v = (v as any).format('YYYY-MM-DD');
        } else if (v instanceof Date && !Number.isNaN(v.getTime())) {
          v = v.toISOString().slice(0, 10);
        } else if (typeof v === 'string') {
          v = v.trim();
        }
      }

      // normalize numbers
      if (
        (def.field_type === CustomFieldTypes.NUMBER || def.field_type === CustomFieldTypes.DECIMAL) &&
        v !== '' &&
        v !== null &&
        v !== undefined
      ) {
        const n = Number(v);
        v = Number.isNaN(n) ? v : n;
      }

      // normalize multi_select
      if (def.field_type === CustomFieldTypes.MULTI_SELECT && !Array.isArray(v)) {
        v = v ? [v] : [];
      }

      // normalize boolean
      if (def.field_type === CustomFieldTypes.BOOLEAN) {
        v = !!v;
      }

      out[name] = v;
    }
    return out;
  }

  private getInitialValue(def: ICustomField): any {
    const key = def.technical_name;
    const values = this.objectCustomFields?.values ?? {};
    const hasValue = Object.prototype.hasOwnProperty.call(values, key);
    if (!hasValue) return this.getDefaultValue(def);

    const v = (values as any)[key];
    if (def.field_type === CustomFieldTypes.MULTI_SELECT) return Array.isArray(v) ? v : v ? [v] : [];
    if (def.field_type === CustomFieldTypes.BOOLEAN) return !!v;
    return v ?? this.getDefaultValue(def);
  }

  private getDefaultValue(def: ICustomField): any {
    if (def.field_type === CustomFieldTypes.MULTI_SELECT) return [];
    if (def.field_type === CustomFieldTypes.BOOLEAN) return false;
    if (def.field_type === CustomFieldTypes.DATE) return null;
    return '';
  }

  private getValidators(def: ICustomField) {
    const v = [];
    if (def.is_required) v.push(Validators.required);
    if (def.field_type === CustomFieldTypes.EMAIL) v.push(Validators.email);
    return v;
  }
}

